import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useFeatures } from "../../context/FeaturesContext";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Lock, Sparkles, AlertTriangle, Loader2, Plus, Minus, Users } from "lucide-react";

const CYCLE_LABEL = { monthly: "Monthly", yearly: "Yearly", one_time: "One-time" };

function StatusBadge({ status }) {
  const map = {
    included: "bg-green-100 text-green-700",
    subscribed: "bg-blue-100 text-blue-700",
    available: "bg-[hsl(var(--kb-surface-alt))] text-[hsl(var(--kb-text))]",
    disabled: "bg-gray-200 text-gray-500",
  };
  return <span className={`text-xs px-2 py-0.5 ${map[status] || ""}`}>{status}</span>;
}

export default function AdminBilling() {
  const [plan, setPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const { refresh: refreshFeatures } = useFeatures();

  const load = async () => {
    try {
      const [p, f, est, inv, seatInfo] = await Promise.all([
        api.get("/tenant/plan"),
        api.get("/tenant/features"),
        api.get("/tenant/billing/estimate"),
        api.get("/tenant/billing/invoices"),
        api.get("/tenant/seller-seats"),
      ]);
      setPlan(p.data);
      setFeatures(f.data);
      setEstimate(est.data);
      setInvoices(inv.data.items || []);
      setSeats(seatInfo.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load billing");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (key, quantity = 1) => {
    setBusyKey(key);
    try {
      const { data } = await api.post(`/tenant/features/${key}/subscribe`,
        { billing_cycle: "monthly", quantity });
      const seatNote = key === "extra_seller_seats" ? ` (${quantity} seat${quantity > 1 ? "s" : ""})` : "";
      toast.success(`Subscribed${seatNote} — ₹${data.prorated_charge} prorated for this cycle`);
      await Promise.all([load(), refreshFeatures()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscribe failed");
    } finally { setBusyKey(null); }
  };

  const unsubscribe = async (key) => {
    setBusyKey(key);
    try {
      const { data } = await api.post(`/tenant/features/${key}/unsubscribe`, {});
      if (data.policy === "immediate") {
        toast.success("Cancelled — access removed immediately");
      } else {
        const date = data.effective ? new Date(data.effective).toLocaleDateString() : "cycle end";
        toast.success(`Cancelled — access continues until ${date}`);
      }
      await Promise.all([load(), refreshFeatures()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsubscribe failed");
    } finally { setBusyKey(null); }
  };

  const updateQuantity = async (key, nextQty) => {
    setBusyKey(key);
    try {
      const { data } = await api.patch(`/tenant/features/${key}/quantity`, { quantity: nextQty });
      if (data.delta > 0) {
        toast.success(`Added ${data.delta} seat${data.delta > 1 ? "s" : ""} — ₹${data.prorated_charge} prorated`);
      } else {
        toast.success(`Reduced by ${Math.abs(data.delta)} seat${Math.abs(data.delta) > 1 ? "s" : ""} — applied at next cycle`);
      }
      await Promise.all([load(), refreshFeatures()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusyKey(null); }
  };

  const generateInvoice = async () => {
    try {
      await api.post("/tenant/billing/generate-invoice");
      toast.success("Invoice generated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return <div className="space-y-4" data-testid="billing-loading">
      <div className="h-40 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />)}
      </div>
    </div>;
  }

  const currentPlan = plan?.plan;
  const currentSub = plan?.subscription;
  const addons = features.filter((f) => f.status === "subscribed");
  const available = features.filter((f) => f.status === "available");
  const included = features.filter((f) => f.status === "included");

  return (
    <div className="space-y-8" data-testid="admin-billing-page">
      <div>
        <h1 className="font-display text-3xl">Billing & Subscriptions</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Your current plan, add-ons, and a live estimate of your next bill.</p>
      </div>

      {/* Current plan + next bill */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="billing-plan-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[hsl(var(--kb-muted))]"><Sparkles size={12} /> Current plan</div>
          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            <div className="font-display text-3xl">{currentPlan?.plan_name || "—"}</div>
            <div className="text-sm text-[hsl(var(--kb-muted))]">
              ₹{currentSub?.price_at_purchase || 0} · {CYCLE_LABEL[currentSub?.billing_cycle] || "—"}
            </div>
          </div>
          <div className="text-sm text-[hsl(var(--kb-muted))] mt-1">{currentPlan?.description}</div>
          {currentSub?.cycle_end_date && (
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-3">
              Current cycle ends on <b>{new Date(currentSub.cycle_end_date).toLocaleDateString()}</b>
            </div>
          )}
          <div className="mt-4 text-sm">
            <div className="font-medium mb-1">{included.length} included features</div>
            <div className="flex flex-wrap gap-1">
              {included.map((f) => (
                <span key={f.feature_key} className="bg-[hsl(var(--kb-surface-alt))] px-2 py-0.5 text-xs flex items-center gap-1">
                  <CheckCircle2 size={10} className="text-green-600" /> {f.feature_name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="billing-estimate-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[hsl(var(--kb-muted))]"><CreditCard size={12} /> Next bill estimate</div>
          <div className="font-display text-3xl mt-2" data-testid="billing-estimate-total">₹{estimate?.total_amount?.toLocaleString() || 0}</div>
          <div className="text-xs text-[hsl(var(--kb-muted))]">{estimate?.currency} · {CYCLE_LABEL[estimate?.billing_cycle]}</div>
          <ul className="mt-4 space-y-1 text-sm" data-testid="billing-line-items">
            {estimate?.line_items?.map((l, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="truncate">{l.name}</span>
                <span>₹{l.amount.toLocaleString()}</span>
              </li>
            ))}
          </ul>
          <button onClick={generateInvoice} className="kb-btn-outline w-full mt-4 text-sm" data-testid="generate-invoice-btn">Generate invoice now</button>
        </div>
      </div>

      {/* Subscribed add-ons */}
      {addons.length > 0 && (
        <section data-testid="billing-addons-section">
          <h2 className="font-display text-2xl mb-3">Your add-ons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addons.map((f) => (
              <div key={f.feature_key} className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid={`addon-${f.feature_key}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg">{f.feature_name}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{f.feature_key}</div>
                  </div>
                  <StatusBadge status={f.status} />
                </div>
                <div className="text-sm text-[hsl(var(--kb-muted))] mt-2 line-clamp-2">{f.description}</div>
                <div className="mt-3 text-sm">₹{f.monthly_price}/mo locked at purchase</div>
                <button
                  className="kb-btn-outline text-sm mt-3 inline-flex items-center gap-2"
                  onClick={() => unsubscribe(f.feature_key)}
                  disabled={busyKey === f.feature_key}
                  data-testid={`addon-cancel-${f.feature_key}`}>
                  {busyKey === f.feature_key ? <Loader2 size={12} className="animate-spin" /> : null}
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Feature marketplace */}
      <section data-testid="billing-marketplace">
        <h2 className="font-display text-2xl mb-3">Feature marketplace</h2>
        {available.length === 0 ? (
          <div className="text-sm text-[hsl(var(--kb-muted))]">You're subscribed to everything available. 🎉</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {available.map((f) => (
              <div key={f.feature_key} className="bg-white border border-[hsl(var(--kb-border))] p-5 flex flex-col" data-testid={`addon-card-${f.feature_key}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg">{f.feature_name}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{f.feature_key}</div>
                  </div>
                  <StatusBadge status={f.status} />
                </div>
                <div className="text-sm text-[hsl(var(--kb-muted))] mt-2 line-clamp-3 flex-1">{f.description}</div>
                <div className="mt-3 text-sm">
                  {f.pricing_type === "usage_based"
                    ? <>₹{f.usage_price}/{f.usage_unit}</>
                    : <>₹{f.monthly_price}/mo · ₹{f.yearly_price}/yr</>}
                </div>
                <button
                  className="kb-btn-primary text-sm mt-3 inline-flex items-center gap-2"
                  onClick={() => subscribe(f.feature_key)}
                  disabled={busyKey === f.feature_key}
                  data-testid={`addon-enable-${f.feature_key}`}>
                  {busyKey === f.feature_key ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                  Enable
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Invoices */}
      <section data-testid="billing-invoices">
        <h2 className="font-display text-2xl mb-3">Invoices</h2>
        {invoices.length === 0 ? (
          <div className="text-sm text-[hsl(var(--kb-muted))]">No invoices yet — generate one from the estimate card to preview the full bill.</div>
        ) : (
          <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--kb-surface-alt))] text-left">
                <tr><th className="p-3">Period</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Items</th></tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-3 text-xs">
                      {new Date(inv.billing_start_date).toLocaleDateString()} – {new Date(inv.billing_end_date).toLocaleDateString()}
                    </td>
                    <td className="p-3 font-medium">₹{inv.total_amount.toLocaleString()}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{inv.status}</span></td>
                    <td className="p-3 text-xs text-[hsl(var(--kb-muted))]">{inv.line_items?.length || 0} items</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
